"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle,
  HelpCircle,
  Loader2,
  Mic,
  MicOff,
  Play,
  RotateCcw,
  Square,
  Volume2,
  X,
} from "lucide-react";
import MicrophoneWaveform from "./MicrophoneWaveform";
import { startAudioQualityMonitor } from "@/lib/interview/audioQuality";

const MIC_STATE = {
  UNCHECKED: "unchecked",
  CHECKING: "checking",
  GRANTED: "granted",
  PASSED: "passed",
  DENIED: "denied",
};

const TEST_SECONDS = 3;
const NOOP = (_deviceId) => {
  void _deviceId;
};

function microphoneError(error, t) {
  const name = error?.name || "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return {
      kind: "permission",
      message: t.mic_error_permission,
    };
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return {
      kind: "missing",
      message: t.mic_error_missing,
    };
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return {
      kind: "busy",
      message: t.mic_error_busy,
    };
  }
  return {
    kind: "system",
    message: t.mic_error_system,
  };
}

function MicrophoneHelp({ t, onClose, activeProblem }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="microphone-help-title"
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <header className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4">
          <div>
            <h2
              id="microphone-help-title"
              className="text-lg font-semibold text-slate-900"
            >
              {t.mic_help_title}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {t.mic_help_subtitle}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label={t.mic_help_close}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="space-y-4 p-5">
          {activeProblem && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <span className="font-semibold">{t.mic_detected_problem}: </span>
              {activeProblem}
            </div>
          )}

          <section className="rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900">Windows</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {t.mic_help_windows}
            </p>
          </section>

          <section className="rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900">macOS</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {t.mic_help_macos}
            </p>
          </section>

          <section className="rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900">{t.mic_help_chrome_title}</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {t.mic_help_chrome}
            </p>
          </section>

          <section className="rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900">Safari</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {t.mic_help_safari}
            </p>
          </section>

          <section className="rounded-xl border border-slate-200 p-4">
            <h3 className="font-semibold text-slate-900">Firefox</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {t.mic_help_firefox}
            </p>
          </section>

          <div className="rounded-xl bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-600">
            {t.mic_help_note}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * STEP 4 — optional microphone recording and playback check.
 * Users can begin first and connect or switch their listening source inside
 * the Answer Workspace when the meeting is ready.
 */
export default function DeviceCheckStep({
  t,
  micState,
  onMicStateChange,
  textOnly,
  onTextOnlyChange,
  audioSupported,
  allowTextOnly = true,
  initialDeviceId = "",
  onDeviceIdChange = NOOP,
}) {
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(initialDeviceId);
  const [testState, setTestState] = useState("idle");
  const [testUrl, setTestUrl] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(TEST_SECONDS);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [error, setError] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const qualityMonitorRef = useRef(null);
  const recordingStartedAtRef = useRef(0);

  useEffect(() => {
    onDeviceIdChange(selectedDeviceId);
  }, [onDeviceIdChange, selectedDeviceId]);

  const stopLevelMonitor = useCallback(() => {
    const result = qualityMonitorRef.current?.stop() ?? null;
    qualityMonitorRef.current = null;
    return result;
  }, []);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const resetRecording = useCallback(() => {
    clearTimer();
    stopLevelMonitor();
    stopStream();
    setTestState("idle");
    setSecondsRemaining(TEST_SECONDS);
    setHasPlayed(false);
    setTestUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return null;
    });
    if (micState === MIC_STATE.PASSED) {
      onMicStateChange(MIC_STATE.GRANTED);
    }
  }, [clearTimer, micState, onMicStateChange, stopLevelMonitor, stopStream]);

  useEffect(() => {
    return () => {
      clearTimer();
      if (recorderRef.current?.state === "recording") {
        try {
          recorderRef.current.stop();
        } catch {
          // Recorder was already stopped.
        }
      }
      stopLevelMonitor();
      stopStream();
      if (audioRef.current) audioRef.current.pause();
    };
  }, [clearTimer, stopLevelMonitor, stopStream]);

  useEffect(() => {
    return () => {
      if (testUrl) URL.revokeObjectURL(testUrl);
    };
  }, [testUrl]);

  const refreshDevices = useCallback(async () => {
    try {
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const microphones = allDevices.filter(
        (device) => device.kind === "audioinput",
      );
      setDevices(microphones);
      setSelectedDeviceId((current) => {
        if (
          current &&
          microphones.some((device) => device.deviceId === current)
        ) {
          return current;
        }
        return microphones[0]?.deviceId || "";
      });
    } catch {
      setDevices([]);
    }
  }, []);

  const getStream = useCallback(
    () =>
      navigator.mediaDevices.getUserMedia({
        audio: selectedDeviceId
          ? { deviceId: { exact: selectedDeviceId } }
          : true,
      }),
    [selectedDeviceId],
  );

  const handleCheck = useCallback(async () => {
    if (!audioSupported) return;
    onMicStateChange(MIC_STATE.CHECKING);
    setError(null);
    try {
      const stream = await getStream();
      stream.getTracks().forEach((track) => track.stop());
      onMicStateChange(MIC_STATE.GRANTED);
      await refreshDevices();
    } catch (caught) {
      const problem = microphoneError(caught, t);
      setError(problem);
      onMicStateChange(MIC_STATE.DENIED);
    }
  }, [audioSupported, getStream, onMicStateChange, refreshDevices, t]);

  const beginLevelMonitor = useCallback((stream) => {
    qualityMonitorRef.current = startAudioQualityMonitor(stream);
  }, []);

  const handleTestRecord = useCallback(async () => {
    if (testState === "recording") {
      if (recorderRef.current?.state === "recording") {
        recorderRef.current.stop();
      }
      return;
    }

    setError(null);
    setHasPlayed(false);
    setSecondsRemaining(TEST_SECONDS);
    setTestUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return null;
    });

    try {
      const stream = await getStream();
      streamRef.current = stream;
      onMicStateChange(MIC_STATE.GRANTED);
      await refreshDevices();
      beginLevelMonitor(stream);
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (event.data?.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        clearTimer();
        const quality = stopLevelMonitor();
        stopStream();
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });
        const recordedForMs = Date.now() - recordingStartedAtRef.current;
        chunksRef.current = [];
        recorderRef.current = null;

        if (recordedForMs < 2500) {
          setError({
            kind: "short",
            message:
              t.mic_test_too_short,
          });
          setTestState("idle");
          setSecondsRemaining(TEST_SECONDS);
          return;
        }

        if (
          blob.size < 1000 ||
          (quality?.analysisAvailable && !quality.passed)
        ) {
          setError({
            kind: "silent",
            message:
              t.mic_test_silent,
          });
          setTestState("idle");
          setSecondsRemaining(TEST_SECONDS);
          return;
        }

        setTestUrl(URL.createObjectURL(blob));
        setTestState("ready");
        setSecondsRemaining(0);
      };
      recordingStartedAtRef.current = Date.now();
      recorder.start(250);
      setTestState("recording");

      let remaining = TEST_SECONDS;
      timerRef.current = window.setInterval(() => {
        remaining -= 1;
        setSecondsRemaining(Math.max(0, remaining));
        if (remaining <= 0) {
          clearTimer();
          if (recorderRef.current?.state === "recording") {
            recorderRef.current.stop();
          }
        }
      }, 1000);
    } catch (caught) {
      clearTimer();
      stopLevelMonitor();
      stopStream();
      const problem = microphoneError(caught, t);
      setError(problem);
      onMicStateChange(MIC_STATE.DENIED);
      setTestState("idle");
    }
  }, [
    beginLevelMonitor,
    clearTimer,
    getStream,
    onMicStateChange,
    refreshDevices,
    stopLevelMonitor,
    stopStream,
    testState,
    t,
  ]);

  const handlePlayback = useCallback(() => {
    if (!testUrl) return;
    if (!audioRef.current) audioRef.current = new Audio();
    const audio = audioRef.current;
    audio.src = testUrl;
    setTestState("playing");
    audio.onended = () => {
      setHasPlayed(true);
      setTestState("ready");
    };
    audio.onerror = () => {
      setError({
        kind: "playback",
        message:
          t.mic_playback_error,
      });
      setTestState("ready");
    };
    audio.play().catch(() => {
      setError({
        kind: "playback",
        message:
          t.mic_playback_blocked,
      });
      setTestState("ready");
    });
  }, [testUrl, t]);

  const confirmHeard = useCallback(() => {
    setError(null);
    onMicStateChange(MIC_STATE.PASSED);
    setTestState("passed");
  }, [onMicStateChange]);

  const chooseTextOnly = useCallback(
    (checked) => {
      if (checked) {
        clearTimer();
        if (recorderRef.current?.state === "recording") {
          recorderRef.current.stop();
        }
        stopLevelMonitor();
        stopStream();
      }
      onTextOnlyChange(checked);
    },
    [clearTimer, onTextOnlyChange, stopLevelMonitor, stopStream],
  );

  const statusMessage =
    micState === MIC_STATE.PASSED
      ? t.mic_test_passed_begin
      : micState === MIC_STATE.GRANTED
        ? t.mic_access_allowed_test
        : micState === MIC_STATE.DENIED
          ? error?.message || t.mic_denied
          : micState === MIC_STATE.CHECKING
            ? t.mic_requesting
            : t.mic_desc;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-900">
        <p className="font-semibold">
          {t.mic_readiness_title}
        </p>
        <p className="mt-1 text-xs leading-5 text-indigo-800">
          {t.mic_readiness_desc}
        </p>
      </div>

      {allowTextOnly && (
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
          <input
            type="checkbox"
            checked={textOnly}
            onChange={(event) => chooseTextOnly(event.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span>
            <span className="block text-sm font-medium text-slate-800">
              {t.text_only_label}
            </span>
            <span className="block text-xs text-slate-500">
              {t.text_only_hint}
            </span>
          </span>
        </label>
      )}

      {!textOnly && (
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white px-4 py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              {micState === MIC_STATE.PASSED ? (
                <CheckCircle className="mt-0.5 h-5 w-5 text-emerald-600" />
              ) : micState === MIC_STATE.DENIED ? (
                <MicOff className="mt-0.5 h-5 w-5 text-red-500" />
              ) : (
                <Mic className="mt-0.5 h-5 w-5 text-indigo-600" />
              )}
              <div>
                <p className="text-sm font-medium text-slate-800">
                  {statusMessage}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {t.mic_permission_note}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setHelpOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <HelpCircle className="h-4 w-4" />
              {t.mic_help_button}
            </button>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error.message}</span>
              <button
                type="button"
                onClick={() => setHelpOpen(true)}
                className="ml-auto shrink-0 font-semibold underline"
              >
                {t.mic_view_help}
              </button>
            </div>
          )}

          {devices.length > 0 && (
            <label className="block">
              <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t.mic_device_label}
              </span>
              <select
                value={selectedDeviceId}
                onChange={(event) => {
                  setSelectedDeviceId(event.target.value);
                  resetRecording();
                  setError(null);
                }}
                disabled={testState === "recording" || testState === "playing"}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:opacity-50"
              >
                {devices.map((device, index) => (
                  <option
                    key={device.deviceId || index}
                    value={device.deviceId}
                  >
                    {device.label || `${t.mic_device_fallback} ${index + 1}`}
                  </option>
                ))}
              </select>
            </label>
          )}

          {!audioSupported ? (
            <div className="rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-600">
              {t.audio_unsupported} {t.mic_unsupported_next}
            </div>
          ) : micState === MIC_STATE.UNCHECKED ||
            micState === MIC_STATE.CHECKING ||
            micState === MIC_STATE.DENIED ? (
            <button
              type="button"
              onClick={handleCheck}
              disabled={micState === MIC_STATE.CHECKING}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {micState === MIC_STATE.CHECKING ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
              {t.mic_check_btn}
            </button>
          ) : (
            <div className="space-y-3 border-t border-slate-100 pt-4">
              {testState === "recording" && streamRef.current && (
                <MicrophoneWaveform
                  stream={streamRef.current}
                  title={t.mic_recording_test
                    .replace("{seconds}", secondsRemaining)}
                  subtitle={t.mic_speak_normally}
                  ariaLabel={t.mic_waveform_aria}
                />
              )}

              <div className="flex flex-wrap items-center gap-2">
                {testState !== "passed" && (
                  <button
                    type="button"
                    onClick={handleTestRecord}
                    disabled={testState === "playing"}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50 ${
                      testState === "recording"
                        ? "bg-red-600 text-white hover:bg-red-700"
                        : "border border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {testState === "recording" ? (
                      <>
                        <Square className="h-4 w-4 fill-white" />
                        {t.test_stop_btn}
                      </>
                    ) : (
                      <>
                        <Mic className="h-4 w-4" />
                        {testUrl ? t.mic_record_again : t.mic_record_three_seconds}
                      </>
                    )}
                  </button>
                )}

                {testUrl &&
                  (testState === "ready" || testState === "playing") && (
                    <button
                      type="button"
                      onClick={handlePlayback}
                      disabled={testState === "playing"}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      {testState === "playing" ? (
                        <Volume2 className="h-4 w-4 animate-pulse" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                      {testState === "playing"
                        ? t.mic_playing_test
                        : t.test_play_btn}
                    </button>
                  )}

                {testState === "passed" && (
                  <button
                    type="button"
                    onClick={resetRecording}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <RotateCcw className="h-4 w-4" />
                    {t.mic_test_another}
                  </button>
                )}
              </div>

              {testUrl && !hasPlayed && testState === "ready" && (
                <p className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-semibold text-indigo-900" role="status">
                  {t.mic_playback_instruction}
                </p>
              )}

              {hasPlayed && testState === "ready" && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                  <p className="text-sm font-semibold text-emerald-900">
                    {t.mic_hear_question}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={confirmHeard}
                      className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                    >
                      <CheckCircle className="h-4 w-4" />
                      {t.mic_hear_yes}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setError({
                          kind: "playback",
                          message:
                            t.mic_hear_no_error,
                        });
                        resetRecording();
                      }}
                      className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-white"
                    >
                      {t.mic_hear_no}
                    </button>
                  </div>
                </div>
              )}

              {micState === MIC_STATE.PASSED && (
                <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800">
                  <CheckCircle className="h-5 w-5" />
                  {t.mic_voice_ready}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {helpOpen && (
        <MicrophoneHelp
          t={t}
          activeProblem={error?.message || null}
          onClose={() => setHelpOpen(false)}
        />
      )}
    </div>
  );
}
